import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {Hotel} from '@/redux/v1/hotels/list/models';

import styled from '@emotion/styled';
import {useBreakpointDown} from '@travel-frontend/core';
import {LabelXSmall, Truncate} from '@travel-frontend/ui-components';
import {isNil} from 'lodash';
import {Trans, useTranslation} from 'react-i18next';

import {Divider} from '@/components/Divider';
import {Container} from '@/components/InfoTrip/components/Container';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {useSimplifiedRoomString, useBoardType} from '@/modules/common/hooks';
import {mapChildrenAgesToString} from '@/utils/mapChildrenAgesToString';

type Props = {
  stays: number | null | undefined;
  overnightStays: number | null | undefined;
  rooms: Hotel['rooms'];
  adultCount: number;
  childrenAges: Array<number>;
  travelType: TravelType;
  shouldShowChildrenAge: boolean;
};

export const InfoTrip = styled(
  ({
    stays,
    overnightStays,
    rooms,
    adultCount,
    childrenAges,
    travelType,
    shouldShowChildrenAge,
    ...restProps
  }: Props) => {
    const {t} = useTranslation(['general']);
    const mdDown = useBreakpointDown('md');
    const roomString = useSimplifiedRoomString(rooms);
    const {boardType, isBoardTypeEqual} = useBoardType(rooms);

    return (
      <Container {...restProps}>
        {(!isNil(stays) || !isNil(overnightStays)) && (
          <LabelXSmall color="contentSecondary">
            <Truncate>
              {!isNil(stays) && (
                <>
                  {t('general:xDays', {count: stays})}
                  <Divider
                    as="span"
                    orientation="vertical"
                    category="secondary"
                  />
                </>
              )}
              {!isNil(overnightStays) &&
                t('general:xNights', {count: overnightStays})}
            </Truncate>
          </LabelXSmall>
        )}
        {(roomString || !isNil(boardType)) && (
          <LabelXSmall color="contentSecondary">
            <Truncate>
              {roomString}
              {roomString && isBoardTypeEqual && (
                <>
                  <Divider
                    as="span"
                    orientation="vertical"
                    category="secondary"
                  />
                  {boardType}
                </>
              )}
            </Truncate>
          </LabelXSmall>
        )}
        {boardType && !isBoardTypeEqual && (
          <LabelXSmall color="contentSecondary">
            <Truncate>{boardType}</Truncate>
          </LabelXSmall>
        )}
        <LabelXSmall color="contentSecondary">
          <Truncate>
            <Trans
              ns="general"
              i18nKey="formatTravellers.ALL"
              components={{
                nowrap: <span />,
              }}
              values={{
                context: shouldShowChildrenAge
                  ? 'withChildAge'
                  : 'withoutChildAge',
                adultCount,
                childrenCount: childrenAges.length,
                childrenAges: mapChildrenAgesToString(childrenAges),
              }}
              shouldUnescape
            />
            {(shouldShowChildrenAge === false || !mdDown) && (
              <>
                <Divider
                  as="span"
                  orientation="vertical"
                  category="secondary"
                />
                {travelType === TravelTypes.packageTour
                  ? t('general:infoForm.packageTour')
                  : t('general:infoForm.ownArrival')}
              </>
            )}
          </Truncate>
        </LabelXSmall>
        {shouldShowChildrenAge && mdDown && (
          <LabelXSmall color="contentSecondary">
            {travelType === TravelTypes.packageTour
              ? t('general:infoForm.packageTour')
              : t('general:infoForm.ownArrival')}
          </LabelXSmall>
        )}
      </Container>
    );
  }
)``;
